
import { useState, useMemo } from 'react';
import { TeacherMonthlyReport } from '@/types/salary';

export function useTeacherSalaryFilters(teacherReports: TeacherMonthlyReport[]) {
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmationStatusFilter, setConfirmationStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const filteredAndSortedReports = useMemo(() => {
    let filtered = teacherReports.filter(report => report.total_salary > 0);

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(report => 
        report.teacher_id.toLowerCase().includes(query) ||
        (report.teacher_name && report.teacher_name.toLowerCase().includes(query))
      );
    }

    // Apply confirmation status filter
    if (confirmationStatusFilter !== 'all') {
      filtered = filtered.filter(report => 
        report.confirmation_status === confirmationStatusFilter
      );
    }

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortField) {
          case 'teacher_id':
            aValue = a.teacher_id;
            bValue = b.teacher_id;
            break;
          case 'teacher_name':
            aValue = a.teacher_name || '';
            bValue = b.teacher_name || '';
            break;
          case 'total_reports':
            aValue = a.total_reports;
            bValue = b.total_reports;
            break;
          case 'approved_worksheet_reports':
            aValue = a.approved_worksheet_reports;
            bValue = b.approved_worksheet_reports;
            break;
          case 'total_salary':
            aValue = a.total_salary;
            bValue = b.total_salary;
            break;
          case 'confirmation_status':
            aValue = a.confirmation_status;
            bValue = b.confirmation_status;
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [teacherReports, searchQuery, confirmationStatusFilter, sortField, sortDirection]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setConfirmationStatusFilter('all');
    setSortField('');
    setSortDirection('asc');
  };

  return {
    searchQuery,
    setSearchQuery,
    confirmationStatusFilter,
    setConfirmationStatusFilter,
    sortField,
    sortDirection,
    filteredAndSortedReports,
    handleSort,
    clearFilters
  };
}
